import {PriceSummaryModel} from './PriceSummary.model';
import {ViolationModel} from './Violation.model';
import {FeeModel} from './Fee.model';
import {MinimumOrderAmountModel} from './MinimumOrderAmount.model';
import {EstimateTotalsResponse} from '@wix/auto_sdk_ecom_current-cart';
import {CouponDiscountModel} from './CouponDiscount.model';
import {toAppliedCouponCartV1} from '../../utils/coupon';
import {ModelContext} from '../../../types/app.types';
import {CouponModel} from '../Cart/Coupon.model';
import {PaymentSummaryModel} from './PaymentSummary.model';
import {PriceModel} from '../Price.model';
import {DeliverySummaryModel} from './DeliverySummary.model';
import {TaxSummaryModel} from './TaxSummary.model';
import {CartSummary, DiscountScope, Source} from '@wix/auto_sdk_ecom_cart-v-2';
import {SPECS} from '../../specs';
import {DiscountModel} from './Discount.model';

export class CartSummaryModel {
  public calculationId?: string;
  public additionalFees: FeeModel[];
  public platformFees: FeeModel[];
  public violations: ViolationModel[];
  public paymentSummary: PaymentSummaryModel;
  public priceSummary: PriceSummaryModel;
  public deliverySummary: DeliverySummaryModel;
  public minimumOrderAmount: MinimumOrderAmountModel;
  public estimatedAmountToPay: number;
  public taxSummary: TaxSummaryModel;
  public globalDiscounts: DiscountModel[];
  public lineItemDiscounts: DiscountModel[];
  /**
   * @deprecated
   */
  public couponDiscount: CouponDiscountModel;
  /**
   * @deprecated
   */
  public appliedCoupon?: CouponModel;

  constructor(params: CartSummaryModel) {
    this.calculationId = params.calculationId;
    this.additionalFees = params.additionalFees;
    this.platformFees = params.platformFees;
    this.violations = params.violations;
    this.paymentSummary = params.paymentSummary;
    this.priceSummary = params.priceSummary;
    this.deliverySummary = params.deliverySummary;
    this.couponDiscount = params.couponDiscount;
    this.minimumOrderAmount = params.minimumOrderAmount;
    this.estimatedAmountToPay = params.estimatedAmountToPay;
    this.taxSummary = params.taxSummary;
    this.globalDiscounts = params.globalDiscounts;
    this.lineItemDiscounts = params.lineItemDiscounts;
  }

  public static fromCartV1(estimatedTotals: EstimateTotalsResponse, modelContext: ModelContext): CartSummaryModel {
    return new CartSummaryModel({
      additionalFees: (estimatedTotals?.additionalFees ?? [])
        .filter((fee) => {
          if (!modelContext.viewerContext.isExperimentEnabled(SPECS.PlatformFeeOnCartAndCheckout)) {
            return true;
          }
          return fee.source !== Source.PLATFORM;
        })
        .map((fee) => FeeModel.fromCartV1(fee, modelContext)),
      platformFees: modelContext.viewerContext.isExperimentEnabled(SPECS.PlatformFeeOnCartAndCheckout)
        ? (estimatedTotals?.additionalFees ?? [])
            .filter((fee) => fee.source === Source.PLATFORM)
            .map((fee) => FeeModel.fromCartV1(fee, modelContext))
        : [],
      violations: (estimatedTotals?.violations ?? []).map(ViolationModel.fromCartV1),
      estimatedAmountToPay: Number(
        modelContext.shouldPayWithConvertedCurrency
          ? estimatedTotals.priceSummary.total.convertedAmount
          : estimatedTotals.priceSummary.total.amount
      ),
      paymentSummary: PaymentSummaryModel.fromCartV1({
        payNow: PriceModel.fromCartV1(estimatedTotals.payNow.total, modelContext),
        payLater: PriceModel.fromCartV1(estimatedTotals.payLater.total, modelContext),
        payAfterFreeTrial: PriceModel.fromCartV1(estimatedTotals.payAfterFreeTrial.total, modelContext),
      }),
      priceSummary: PriceSummaryModel.fromCartV1(estimatedTotals.priceSummary, modelContext),
      deliverySummary: DeliverySummaryModel.fromCartV1(estimatedTotals.shippingInfo?.selectedCarrierServiceOption),
      couponDiscount: modelContext.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)
        ? undefined
        : CouponDiscountModel.fromCartV1(estimatedTotals.appliedDiscounts),
      minimumOrderAmount: MinimumOrderAmountModel.fromCartV1(
        estimatedTotals.calculationErrors.orderValidationErrors,
        modelContext
      ),
      taxSummary: TaxSummaryModel.fromCartV1(estimatedTotals.taxSummary, estimatedTotals.priceSummary, modelContext),
      appliedCoupon: toAppliedCouponCartV1(estimatedTotals.appliedDiscounts),
      globalDiscounts: modelContext.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)
        ? estimatedTotals.appliedDiscounts
            .map((discount) => DiscountModel.fromCartV1(discount))
            .filter((discount) => discount !== undefined)
        : [],
      lineItemDiscounts: [],
    });
  }

  public static fromCartV2(cartSummary: CartSummary, modelContext: ModelContext): CartSummaryModel {
    return new CartSummaryModel({
      calculationId: cartSummary.calculationId,
      additionalFees: (cartSummary.additionalFees ?? [])
        .filter((additionalFee) => {
          if (!modelContext.viewerContext.isExperimentEnabled(SPECS.PlatformFeeOnCartAndCheckout)) {
            return true;
          }
          return additionalFee.source !== Source.PLATFORM;
        })
        .map((additionalFee) => FeeModel.fromCartV2(additionalFee, modelContext)),
      platformFees: modelContext.viewerContext.isExperimentEnabled(SPECS.PlatformFeeOnCartAndCheckout)
        ? (cartSummary.additionalFees ?? [])
            .filter((additionalFee) => additionalFee.source === Source.PLATFORM)
            .map((additionalFee) => FeeModel.fromCartV2(additionalFee, modelContext))
        : [],
      violations: (cartSummary.spiViolations ?? []).map((violation) => ViolationModel.fromCartV2(violation)),
      estimatedAmountToPay: Number(
        modelContext.shouldPayWithConvertedCurrency
          ? cartSummary.priceSummary.total.convertedAmount
          : cartSummary.priceSummary.total.amount
      ),
      paymentSummary: PaymentSummaryModel.fromCartV2(cartSummary.paymentSummary, modelContext),
      priceSummary: PriceSummaryModel.fromCartV2(cartSummary.priceSummary, modelContext),
      deliverySummary: DeliverySummaryModel.fromCartV2(cartSummary.deliverySummary),
      couponDiscount: modelContext.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)
        ? undefined
        : CouponDiscountModel.fromCartV2(cartSummary.discounts),
      minimumOrderAmount: MinimumOrderAmountModel.fromCartV1(
        cartSummary.calculationErrors.orderValidationErrors,
        modelContext
      ),
      taxSummary: TaxSummaryModel.fromCartV2(cartSummary.taxSummary, modelContext),
      globalDiscounts: modelContext.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)
        ? (cartSummary.discounts ?? /* istanbul ignore next */ [])
            .filter((discount) => discount.scope !== DiscountScope.LINE_ITEM)
            .map((discount) => DiscountModel.fromCartV2(discount))
        : [],
      lineItemDiscounts: modelContext.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)
        ? (cartSummary.discounts ?? /* istanbul ignore next */ [])
            .filter((discount) => discount.scope === DiscountScope.LINE_ITEM)
            .map((discount) => DiscountModel.fromCartV2(discount))
        : [],
    });
  }
}
